import React, { useEffect, useState } from "react";
import { Card, Button, Table, Tag, Select, Space, message, Typography, Statistic, Row, Col, Modal, Form, Input, InputNumber, AutoComplete, Radio } from "antd";
import Alert from "../components/GxAlert";
import { PlusOutlined, SwapOutlined } from "@ant-design/icons";
import ImportExportBar from "../components/ImportExportBar";
import ExportButton from "../components/ExportButton";
import EditableTable from "../components/EditableTable";
import api, {
  createPieceRate, previewPieceRateImport, importPieceRate,
  pieceRateUnitFixPreview, pieceRateUnitFix,
} from "../api/client";
import { byNum, byStr, byDate } from "../utils/sorters";
import { todayISO, JalaliDateInput, toJalali } from "../utils/dateUtils";
import { formatMoney, MoneyInput } from "../utils/money";
import { faNum } from "../utils/faNumberFormat";

/* آدرسِ API از کلاینتِ مشترک (client.js) — قبلاً اینجا نمونهٔ محلیِ axios با
   پورتِ ۸۰۰۰ هاردکد بود و روی HTTPS (پورتِ بسته) «خطا در بارگذاری» می‌داد. */
const { Title, Text } = Typography;
const { Option } = Select;

const fa = (v) => (v == null ? "—" : faNum(Number(v)));

const ACTION_META = {
  update: { l: "به‌روزرسانی نرخ", c: "gold" },
  insert: { l: "عملیات جدید", c: "green" },
  unchanged: { l: "بدون تغییر", c: "default" },
  skip_manual: { l: "دستی — دست‌نخورده", c: "blue" },
};

export default function PieceRatePage() {
  const [records, setRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [filterProduct, setFilterProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [effectiveFrom, setEffectiveFrom] = useState(todayISO());
  const [fileUnit, setFileUnit] = useState("auto");
  const [addModal, setAddModal] = useState(false);
  const [addForm] = Form.useForm();
  // پیش‌نمایش‌ها: تا تأیید نشوند هیچ چیزی ذخیره نمی‌شود
  const [preview, setPreview] = useState(null);      // {data, file}
  const [unitFix, setUnitFix] = useState(null);      // پیش‌نمایشِ تومان→ریال
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { active_only: true };
      if (filterProduct) params.product_type = filterProduct;
      const [recRes, prodRes] = await Promise.all([
        api.get("/api/v1/piece-rate/", { params }),
        api.get("/api/v1/piece-rate/products"),
      ]);
      setRecords(recRes.data);
      setProducts(prodRes.data);
    } catch {
      message.error("خطا در بارگذاری نرخنامه");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterProduct]);

  // ---- ایمپورت: اول پیش‌نمایش، بعد تأیید ----
  const handleImport = async (file) => {
    setLoading(true);
    try {
      const res = await previewPieceRateImport(file, { effective_from: effectiveFrom, unit: fileUnit });
      setPreview({ data: res.data, file });
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا در خواندن فایل");
    }
    setLoading(false);
    return false;
  };

  const confirmImport = async () => {
    setApplying(true);
    try {
      const res = await importPieceRate(preview.file, { effective_from: effectiveFrom, unit: fileUnit });
      setImportResult({ message: res.data.message });
      message.success(res.data.message);
      setPreview(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === "string" ? d : d?.message || "خطا در ایمپورت — هیچ تغییری ذخیره نشد");
    }
    setApplying(false);
  };

  // ---- اصلاح نرخ‌های تومانیِ قبلی ----
  const openUnitFix = async () => {
    setLoading(true);
    try {
      const res = await pieceRateUnitFixPreview();
      if (!res.data.affected) {
        message.info("همهٔ نرخ‌ها از قبل ریالی هستند — چیزی برای اصلاح نیست");
      } else {
        setUnitFix(res.data);
      }
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا در پیش‌نمایش اصلاح");
    }
    setLoading(false);
  };

  const confirmUnitFix = async () => {
    setApplying(true);
    try {
      const res = await pieceRateUnitFix();
      message.success(res.data.message);
      setUnitFix(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === "string" ? d : d?.message || "خطا در اصلاح — هیچ تغییری ذخیره نشد");
    }
    setApplying(false);
  };

  const handleInlineSave = async (id, changes) => {
    await api.patch(`/api/v1/piece-rate/${id}`, changes);
    load();
  };

  const handleDelete = async (id) => {
    await api.delete(`/api/v1/piece-rate/${id}`);
    load();
  };

  const handleAddOperation = async () => {
    const v = await addForm.validateFields();
    const isCut = v.operation_kind === "cut";
    try {
      await createPieceRate({
        product_type: isCut ? "برش" : v.product_type,
        operation_name: isCut ? (v.operation_name || "برش") : v.operation_name,
        stitch_lines: isCut ? 1 : (v.stitch_lines || 1),   // نرخ برش per جین است، خط ندارد
        rate_per_line: Number(v.rate_per_line),
        effective_from: effectiveFrom,
        operation_kind: v.operation_kind || "sewing",
      });
      message.success("عملیات افزوده شد");
      setAddModal(false);
      addForm.resetFields();
      load();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const columns = [
    {
      title: "محصول", dataIndex: "product_type", width: 220, editType: "text", sorter: byStr("product_type"),
      render: (v) => {
        const parts = v.split("—");
        return <span><Tag color="blue" style={{ marginLeft: 4 }}>{parts[0]?.trim()}</Tag>{parts[1]?.trim()}</span>;
      },
    },
    { title: "عملیات", dataIndex: "operation_name", editType: "text", sorter: byStr("operation_name") },
    { title: "نوع", dataIndex: "operation_kind", width: 90, align: "center",
      render: (v) => (v === "cut"
        ? <Tag color="volcano">برش (فی جین)</Tag>
        : <Tag color="cyan">دوخت</Tag>) },
    { title: "تعداد خط", dataIndex: "stitch_lines", width: 100, align: "center", editType: "number", sorter: byNum("stitch_lines") },
    { title: "فی (ریال)", dataIndex: "rate_per_line", width: 130, align: "left", editType: "number", sorter: byNum("rate_per_line"),
      render: (v) => formatMoney(v) },
    { title: "تکمیلی (ریال)", dataIndex: "total_rate", width: 140, align: "left", sorter: byNum("total_rate"),
      render: (v) => <b style={{ color: "#107C41" }} className="gx-money">{formatMoney(v)}</b> },
    { title: "منبع", dataIndex: "source", width: 90, align: "center", sorter: byStr("source"),
      render: (v) => <Tag color={v === "manual" ? "purple" : "default"}>{v === "manual" ? "دستی" : "ایمپورت"}</Tag> },
    { title: "از تاریخ", dataIndex: "effective_from", width: 110, sorter: byDate("effective_from"),
      render: (v) => toJalali(v) },
  ];

  // آمار
  const totalProducts = new Set(records.map((r) => r.product_type)).size;
  const avgRate = records.length
    ? Math.round(records.reduce((s, r) => s + Number(r.total_rate), 0) / records.length)
    : 0;
  const tomanLeft = records.filter((r) => r.rate_unit === "toman").length;

  const previewColumns = [
    { title: "وضعیت", dataIndex: "action", width: 150,
      render: (v) => <Tag color={(ACTION_META[v] || {}).c}>{(ACTION_META[v] || {}).l || v}</Tag> },
    { title: "محصول", dataIndex: "product_type", width: 200, ellipsis: true },
    { title: "عملیات", dataIndex: "operation_name", width: 150, ellipsis: true },
    { title: "خط", dataIndex: "stitch_lines", width: 60, align: "center" },
    { title: "فی قبلی", dataIndex: "old_rate_per_line", width: 110, align: "left", render: (v) => formatMoney(v) },
    { title: "فی جدید", dataIndex: "new_rate_per_line", width: 110, align: "left",
      render: (v, r) => <b style={{ color: r.action === "skip_manual" ? "#999" : "#107C41" }}>{fa(v)}</b> },
    { title: "تکمیلی قبلی", dataIndex: "old_total_rate", width: 120, align: "left", render: (v) => formatMoney(v) },
    { title: "تکمیلی جدید", dataIndex: "new_total_rate", width: 120, align: "left",
      render: (v, r) => <b style={{ color: r.action === "skip_manual" ? "#999" : "#107C41" }}>{fa(v)}</b> },
  ];

  const fixColumns = [
    { title: "محصول", dataIndex: "product_type", width: 200, ellipsis: true },
    { title: "عملیات", dataIndex: "operation_name", width: 150, ellipsis: true },
    { title: "خط", dataIndex: "stitch_lines", width: 60, align: "center" },
    { title: "فی قبلی", dataIndex: "old_rate_per_line", width: 110, align: "left", render: (v) => formatMoney(v) },
    { title: "فی صحیح", dataIndex: "new_rate_per_line", width: 110, align: "left",
      render: (v) => <b style={{ color: "#107C41" }}>{fa(v)}</b> },
    { title: "تکمیلی قبلی", dataIndex: "old_total_rate", width: 120, align: "left", render: (v) => formatMoney(v) },
    { title: "تکمیلی صحیح", dataIndex: "new_total_rate", width: 120, align: "left",
      render: (v) => <b style={{ color: "#107C41" }}>{fa(v)}</b> },
    { title: "فعال", dataIndex: "is_active", width: 70, align: "center",
      render: (v) => (v ? <Tag color="green">فعال</Tag> : <Tag>قدیمی</Tag>) },
  ];

  const impactAlert = (impact) => {
    if (!impact) return null;
    const periods = impact.calculated_periods || [];
    return (
      <Alert
        type={impact.recalculation_required ? "warning" : "info"}
        showIcon
        style={{ marginBottom: 12 }}
        message={impact.recalculation_required
          ? "دوره‌های محاسبه‌شده نیاز به بررسی دارند"
          : "دورهٔ محاسبه‌شده‌ای تحتِ تأثیر نیست"}
        description={
          <div style={{ fontSize: 12 }}>
            <div>{impact.note}</div>
            <div style={{ marginTop: 4 }}>
              رکوردهای تولید: <b>{fa(impact.production_records)}</b>
              {periods.length > 0 && (
                <> — دوره‌های محاسبه‌شده: {periods.map((p) => `${p.year}/${p.month}`).join("، ")}</>
              )}
            </div>
          </div>
        }
      />
    );
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>نرخنامه دوخت <Text type="secondary" style={{ fontSize: 12 }}>(همهٔ مبالغ به ریال)</Text></Title>
      </div>

      {tomanLeft > 0 && (
        <Alert
          type="error" showIcon style={{ marginBottom: 12 }}
          message={`${fa(tomanLeft)} ردیفِ نرخ هنوز تومانی است (یک‌دهمِ مقدارِ درست)`}
          description="با دکمهٔ «اصلاح تومان → ریال» ابتدا پیش‌نمایش بگیرید و بعد اصلاح کنید."
          action={<Button size="small" danger icon={<SwapOutlined />} onClick={openUnitFix}>اصلاح تومان → ریال</Button>}
        />
      )}

      <Card title="ایمپورت نرخنامه جدید" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Space wrap>
            <span>تاریخ اعتبار:</span>
            <JalaliDateInput value={effectiveFrom} onChange={setEffectiveFrom} />
            <span style={{ marginRight: 12 }}>واحد مبالغ فایل:</span>
            <Radio.Group value={fileUnit} onChange={(e) => setFileUnit(e.target.value)} size="small" buttonStyle="solid">
              <Radio.Button value="auto">تشخیص خودکار</Radio.Button>
              <Radio.Button value="rial">ریال</Radio.Button>
              <Radio.Button value="toman">تومان (×۱۰)</Radio.Button>
            </Radio.Group>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            واحدِ پایهٔ برنامه ریال است؛ فایلِ نرخنامهٔ کارخانه هم ریالی است و بدون تبدیل ذخیره می‌شود. اگر فایل ریالی باشد مبالغ ×۱۰ می‌شوند.
            ایمپورت، عملیاتِ موجود را <b>به‌روزرسانی</b> می‌کند (ردیفِ تکراری نمی‌سازد) و ردیف‌های دستی را دست نمی‌زند.
          </Text>
          <ImportExportBar
            templateUrl="/api/v1/templates/piece-rate"
            templateLabel="نرخنامه دوخت"
            onImport={(file) => handleImport(file)}
            importLabel="ورود اطلاعات (با پیش‌نمایش)"
            loading={loading}
            result={importResult ? { message: importResult.message } : null}
          />
        </Space>
      </Card>

      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} sm={8} md={6}>
          <Card size="small">
            <Statistic title="تعداد ردیف نرخ" value={records.length} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card size="small">
            <Statistic title="تعداد محصول" value={totalProducts} />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card size="small">
            <Statistic title="میانگین تکمیلی (ریال)" value={faNum(avgRate)} />
          </Card>
        </Col>
      </Row>

      <Card
        title="لیست نرخ‌ها (ریال)"
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { addForm.resetFields(); setAddModal(true); }}>
              افزودن عملیات
            </Button>
            <Button icon={<SwapOutlined />} onClick={openUnitFix}>اصلاح تومان → ریال</Button>
            <ExportButton url="/api/v1/export/piece-rate" label="نرخنامه" />
            <Select allowClear placeholder="فیلتر محصول" style={{ width: 220 }} onChange={setFilterProduct} value={filterProduct}>
              {products.map((p) => <Option key={p} value={p}>{p}</Option>)}
            </Select>
          </Space>
        }
      >
        <EditableTable
          dataSource={records}
          columns={columns}
          onSave={handleInlineSave}
          onDelete={handleDelete}
          loading={loading}
          pagination={{ pageSize: 30 }}
        />
      </Card>

      {/* پیش‌نمایشِ ایمپورت */}
      <Modal
        title="پیش‌نمایش ایمپورت نرخنامه — تا تأیید نکنید چیزی ذخیره نمی‌شود"
        open={!!preview}
        onOk={confirmImport}
        onCancel={() => setPreview(null)}
        okText="تأیید و اعمال" cancelText="انصراف"
        confirmLoading={applying}
        width={1100}
        okButtonProps={{ disabled: !preview?.data?.rows?.length }}
      >
        {preview && (
          <>
            <Alert
              type={preview.data.converted_toman_to_rial ? "warning" : "info"}
              showIcon style={{ marginBottom: 12 }}
              message={preview.data.converted_toman_to_rial
                ? "مبالغ فایل ریال تشخیص داده شد → همه ×۱۰ و به ریال تبدیل شدند"
                : "مبالغ فایل ریال تشخیص داده شد → بدون تبدیل"}
              description={
                <span style={{ fontSize: 12 }}>
                  فرمت: {preview.data.format === "legacy" ? "فایل کارخانه (دو بلوکی)" : "خروجی برنامه"} — دلیل تشخیص: {preview.data.unit_reason}
                  {" | "}ردیف فایل: <b>{fa(preview.data.file_rows)}</b>
                  {preview.data.duplicates_in_file > 0 && <> | ردیف تکراریِ حذف‌شده: <b>{fa(preview.data.duplicates_in_file)}</b></>}
                </span>
              }
            />
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag color="gold">به‌روزرسانی: {fa(preview.data.summary.update)}</Tag>
              <Tag color="green">جدید: {fa(preview.data.summary.insert)}</Tag>
              <Tag>بدون تغییر: {fa(preview.data.summary.unchanged)}</Tag>
              <Tag color="blue">دستی (دست‌نخورده): {fa(preview.data.summary.skip_manual)}</Tag>
            </Space>
            {impactAlert(preview.data.payroll_impact)}
            <Table
              size="small" rowKey={(r, i) => `${r.product_type}-${r.operation_name}-${i}`}
              columns={previewColumns} dataSource={preview.data.rows}
              pagination={{ pageSize: 12, showSizeChanger: false }}
              scroll={{ x: "max-content" }}
            />
          </>
        )}
      </Modal>

      {/* پیش‌نمایشِ اصلاحِ تومان→ریال */}
      <Modal
        title="اصلاح نرخ‌های تومانی به ریال (×۱۰) — پیش‌نمایش"
        open={!!unitFix}
        onOk={confirmUnitFix}
        onCancel={() => setUnitFix(null)}
        okText="تأیید و اصلاح تراکنشی" cancelText="انصراف"
        okButtonProps={{ danger: true }}
        confirmLoading={applying}
        width={1100}
      >
        {unitFix && (
          <>
            <Alert
              type="warning" showIcon style={{ marginBottom: 12 }}
              message={`${fa(unitFix.affected)} ردیف اصلاح می‌شود (نمونه: ۱۰,۰۰۰ ← ۱۰۰,۰۰۰ ریال)`}
              description={
                <span style={{ fontSize: 12 }}>
                  از قبل ریالی: <b>{fa(unitFix.already_rial)}</b> ردیف |
                  اصلاح <b>تراکنشی</b> است: اگر یک ردیف خطا بدهد، هیچ‌کدام ذخیره نمی‌شوند.
                </span>
              }
            />
            {impactAlert(unitFix.payroll_impact)}
            <Table
              size="small" rowKey="id" columns={fixColumns} dataSource={unitFix.rows}
              pagination={{ pageSize: 12, showSizeChanger: false }}
              scroll={{ x: "max-content" }}
            />
          </>
        )}
      </Modal>

      <Modal
        title="افزودن عملیات دستی به نرخنامه"
        open={addModal}
        onOk={handleAddOperation}
        onCancel={() => setAddModal(false)}
        okText="افزودن" cancelText="انصراف"
      >
        <Form form={addForm} layout="vertical" style={{ direction: "rtl" }}>
          <Form.Item name="operation_kind" label="نوع نرخ" initialValue="sewing">
            <Radio.Group optionType="button" buttonStyle="solid">
              <Radio.Button value="sewing">دوخت (فی هر خط)</Radio.Button>
              <Radio.Button value="cut">برش (فی هر جین)</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item shouldUpdate={(p, c) => p.operation_kind !== c.operation_kind} noStyle>
            {({ getFieldValue }) => {
              const isCut = getFieldValue("operation_kind") === "cut";
              return (
                <Form.Item name="product_type" label={isCut ? "گروه نرخ" : "محصول"}
                  rules={[{ required: true }]}
                  initialValue={isCut ? "برش" : undefined}
                  extra={isCut ? "فی برش برای همهٔ محصولات یکی است — نیازی به انتخاب محصول نیست." : null}>
                  {isCut ? (
                    <Input disabled />
                  ) : (
                    <AutoComplete
                      options={products.map((p) => ({ value: p }))}
                      placeholder="نام محصول (موجود یا جدید)"
                      filterOption={(inp, opt) => opt.value.includes(inp)}
                    />
                  )}
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item name="operation_name" label="نام عملیات" rules={[{ required: true }]}>
            <Input placeholder="مثلاً زیگزاگ دوبل، کش‌دوزی، بغل‌دوزی" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item shouldUpdate={(p, c) => p.operation_kind !== c.operation_kind} noStyle>
                {({ getFieldValue }) => (getFieldValue("operation_kind") === "cut" ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    نرخ برش per جین است و تعداد خط ندارد؛ مبلغ = جین × فی جین + تکی × (فی جین ÷ ۱۲).
                  </Text>
                ) : (
                  <Form.Item name="stitch_lines" label="تعداد خط" initialValue={1}>
                    <InputNumber style={{ width: "100%" }} min={0} step={0.5} />
                  </Form.Item>
                ))}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rate_per_line" label="فی (ریال)"
                rules={[{ required: true, message: "فی (ریال) را وارد کنید" },
                        { type: "number", min: 1, message: "فی باید بزرگ‌تر از صفر باشد", transform: (v) => Number(v) }]}
                extra="مبلغ هر خط؛ مبلغ واحد = فی × تعداد خط">
                <MoneyInput />
              </Form.Item>
              <Form.Item shouldUpdate={(p, c) => p.rate_per_line !== c.rate_per_line || p.stitch_lines !== c.stitch_lines || p.operation_kind !== c.operation_kind} noStyle>
                {() => {
                  const kind = addForm.getFieldValue("operation_kind") || "sewing";
                  const rate = Number(addForm.getFieldValue("rate_per_line") || 0);
                  const lines = kind === "cut" ? 1 : Number(addForm.getFieldValue("stitch_lines") || 1);
                  if (!rate) return null;
                  return (
                    <div className="gx-money-preview" style={{ marginTop: -8, marginBottom: 12 }}>
                      مبلغ هر واحد: {formatMoney(rate * lines, { unit: true })}
                      {kind !== "cut" && ` (= فی × ${faNum(lines)} خط)`}
                    </div>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
